
import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const screenshots = [
    { src: "/screenshots/admin-dashboard.webp", caption: "Performance Dashboard" },
    { src: "/screenshots/menu-management.webp", caption: "Easy Menu Management" },
    { src: "/screenshots/chatbot-analytics.webp", caption: "Customer Insights & Analytics" },
    { src: "/screenshots/chatbot-theme-editor.webp", caption: "Customize Your Chatbot" },
    { src: "/screenshots/pricing-plans.webp", caption: "Flexible Pricing Options" },
    { src: "/screenshots/user-inputs.webp", caption: "Customer Preferences" },
    { src: "/screenshots/preferences-input.webp", caption: "Personalized Recommendations" },
    { src: "/screenshots/recommendation-flow.webp", caption: "AI Food Suggestions" },
];

export default function LivePreview() {
    const [selectedImage, setSelectedImage] = useState<number | null>(null);

    useEffect(() => {
        if (selectedImage === null) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setSelectedImage(null);
            if (event.key === "ArrowRight") {
                setSelectedImage((current) =>
                    current === null ? null : (current + 1) % screenshots.length
                );
            }
            if (event.key === "ArrowLeft") {
                setSelectedImage((current) =>
                    current === null
                        ? null
                        : (current - 1 + screenshots.length) % screenshots.length
                );
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [selectedImage]);

    return (
        <section
            id="product-gallery"
            className="py-20 bg-gradient-to-br from-blue-50 to-white scroll-mt-20"
        >
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
          <span className="text-blue-700 font-semibold text-sm uppercase tracking-wider">
            Product Showcase
          </span>

                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                        See What SavoryBot Can Do
                    </h2>

                    <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                        Explore how SavoryBot helps restaurants manage menus,
                        recommend dishes, and understand customer preferences.
                    </p>
                </div>

                <Swiper
                    modules={[Pagination, Navigation]}
                    navigation
                    pagination={{ clickable: true }}
                    spaceBetween={24}
                    slidesPerView={1}
                    breakpoints={{
                        768: { slidesPerView: 2 },
                        1200: { slidesPerView: 3 },
                    }}
                    className="pb-12"
                >
                    {screenshots.map((item, index) => (
                        <SwiperSlide key={item.src}>
                            <button
                                type="button"
                                onClick={() => setSelectedImage(index)}
                                className="w-full text-left bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition"
                            >
                                <div className="h-[380px] bg-gray-50 flex items-center justify-center p-4">
                                    <img
                                        src={item.src}
                                        alt={item.caption}
                                        loading="lazy"
                                        className="max-w-full max-h-full object-contain"
                                    />
                                </div>

                                <div className="p-5 text-center border-t border-gray-100">
                                    <h3 className="font-semibold text-gray-900">
                                        {item.caption}
                                    </h3>
                                    <p className="text-sm text-blue-700 mt-1">
                                        Click to enlarge
                                    </p>
                                </div>
                            </button>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            {selectedImage !== null && (
                <div
                    role="presentation"
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={screenshots[selectedImage].caption}
                        className="relative flex max-h-[95vh] w-full max-w-6xl flex-col items-center"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            aria-label="Close image"
                            onClick={() => setSelectedImage(null)}
                            className="absolute right-2 top-2 z-10 rounded-full bg-black/70 px-4 py-2 text-white hover:bg-black"
                        >
                            ✕
                        </button>

                        <img
                            src={screenshots[selectedImage].src}
                            alt={screenshots[selectedImage].caption}
                            className="max-h-[85vh] max-w-full object-contain"
                        />

                        <p className="mt-3 text-center text-white">
                            {screenshots[selectedImage].caption}
                        </p>

                        <div className="mt-3 flex gap-4">
                            <button
                                type="button"
                                onClick={() =>
                                    setSelectedImage(
                                        (selectedImage - 1 + screenshots.length) %
                                        screenshots.length
                                    )
                                }
                                className="rounded-lg bg-white/20 px-5 py-2 text-white hover:bg-white/30"
                            >
                                ← Previous
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    setSelectedImage(
                                        (selectedImage + 1) % screenshots.length
                                    )
                                }
                                className="rounded-lg bg-white/20 px-5 py-2 text-white hover:bg-white/30"
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}



{/*
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "react-image-lightbox/style.css";

import React, { useState } from "react";
import Lightbox from "react-image-lightbox";

const screenshots = [
    {src: "/screenshots/admin-dashboard.webp", caption: "Performance Dashboard"},
    {src: "/screenshots/menu-management.webp", caption: "Easy Menu Management"},
    {src: "/screenshots/chatbot-analytics.webp", caption: "Real-time Analytics"},
    {src: "/screenshots/chatbot-theme-editor.webp", caption: "Customize the Chatbot"},
    {src: "/screenshots/pricing-plans.webp", caption: "Flexible Pricing Options"},
    {src: "/screenshots/user-inputs.webp", caption: "Smart Preferences Input"},
    {src: "/screenshots/preferences-input.webp", caption: "Smart Preferences Input"},
    {src: "/screenshots/recommendation-flow.webp", caption: "AI Food Suggestions"},
];

export default function LivePreview() {
    const [isOpen, setIsOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);

    return (
        <section className="py-20 bg-gradient-to-br from-blue-50 to-white" data-aos="fade-up">
            <div className="container mx-auto px-4 text-center">
                <h2 className="text-3xl font-bold text-gray-800 mb-10">🚀 See SavoryBot in Action</h2>
                <p className="text-gray-500 text-sm mb-10">
                    Click any image below to view full screen
                </p>

                <Swiper
                    modules={[Pagination, Autoplay]}
                    pagination={{clickable: true}}
                    autoplay={{delay: 4000}}
                    spaceBetween={30}
                    slidesPerView={1}
                    loop={true}
                    breakpoints={{
                        640: {slidesPerView: 1},
                        768: {slidesPerView: 2},
                        1024: {slidesPerView: 3},
                    }}
                >
                    {screenshots.map((item, index) => (
                        <SwiperSlide key={index}>
                            <div className="px-2">
                                <div
                                    className="bg-gray-50 border border-gray-200 ring-1 ring-gray-100 rounded-xl shadow-md overflow-hidden hover:shadow-xl transform transition duration-300 hover:scale-105 cursor-pointer"
                                    onClick={() => {
                                        setPhotoIndex(index);
                                        setIsOpen(true);
                                    }}
                                >
                                    <img
                                        src={item.src}
                                        alt={item.caption}
                                        className="w-full h-[500px] object-cover transition-transform duration-300 ease-in-out hover:scale-110"
                                    />
                                    <div className="p-4">
                                        <p className="text-blue-800 font-medium">{item.caption}</p>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>

                {isOpen && (
                    <Lightbox
                        mainSrc={screenshots[photoIndex].src}
                        nextSrc={screenshots[(photoIndex + 1) % screenshots.length].src}
                        prevSrc={screenshots[(photoIndex + screenshots.length - 1) % screenshots.length].src}
                        onCloseRequest={() => setIsOpen(false)}
                        onMovePrevRequest={() =>
                            setPhotoIndex((photoIndex + screenshots.length - 1) % screenshots.length)
                        }
                        onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % screenshots.length)}
                        imageCaption={screenshots[photoIndex].caption}
                    />
                )}
            </div>
        </section>
    );
}
*/}

